import React, { useEffect, useState } from "react";
import {
  fetchBestSupScoreProducts,
  fetchTopSearchedProducts,
} from "./api/product";
import PreviewItems from "./components/product/PreviewItems";
import { render } from "./utils/render";
import { useMediaQuery } from "react-responsive";
import {
  mobileMediaQuery,
  tabletVerticalMediaQuery,
} from "./utils/media-queries";
import HighlightCircle from "./components/index/HighlightCircle";
import InfoGraphicItems from "./components/index/InfoGraphicItems";
import ApplicationIndicationItems from "./components/index/ApplicationIndicationItems";

const useProducts = (fetchFunction) => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const fetchedProducts = await fetchFunction();
        setProducts(fetchedProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [fetchFunction]);

  return products;
};

const ProductPreviewContainer = ({
  title,
  fetchFunction,
  scoreType,
  previewItemWidth,
}) => {
  const isMobile = useMediaQuery(mobileMediaQuery());
  const isTabletV = useMediaQuery(tabletVerticalMediaQuery());
  const products = useProducts(fetchFunction);
  const imagesBaseUrl = "https://www.supplemento.de/i";

  return (
    (isMobile || isTabletV) && (
      <PreviewItems
        title={title}
        products={products}
        scoreType={scoreType}
        imagesBaseUrl={imagesBaseUrl}
        previewItemWidth={previewItemWidth}
      />
    )
  );
};

const InfoGraphicContainer = () => {
  const isMobile = useMediaQuery(mobileMediaQuery());
  const isTabletV = useMediaQuery(tabletVerticalMediaQuery());

  const images = [
    {
      src: "/img/Infografic-23-281-420.webp",
      alt: "Experten Check",
    },
    {
      src: "/img/Infografic-24-281-488.webp",
      alt: "Schnell & einfach",
    },
    {
      src: "/img/Infografic-28-281-625.webp",
      alt: "Sicherheit & Vertrauen",
    },
    {
      src: "/img/Infografic-29.webp",
      alt: "Wissenschaftlich fundiertes Bewertungsmodel",
    },
    {
      src: "/img/Infografic-27-281-498.webp",
      alt: "Unabhängig & transparent",
    },
    {
      src: "/img/Infografic-30.webp",
      alt: "Kostenlos",
    },
  ];

  return (
    (isMobile || isTabletV) && (
      <InfoGraphicItems
        title="Was ist <strong>supplemento.de</strong>?"
        subtitle="Warum kann ich mich auf die Bewertung verlassen?"
        images={images}
        itemWidth={165}
      />
    )
  );
};

const ApplicationIndicationContainer = () => {
  const isMobile = useMediaQuery(mobileMediaQuery());
  const isTabletV = useMediaQuery(tabletVerticalMediaQuery());

  const applicationIndications = [
    {
      label: "Anti Aging",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-03.webp",
      url: "/produktsuche/anwendung/antiaging",
    },
    {
      label: "Augen-gesundheit",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-08.webp",
      url: "/produktsuche/anwendung/augengesundheit-sehkraft",
    },
    {
      label: "Entgiftung/Detox",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-10.webp",
      url: "/produktsuche/anwendung/entgiftung",
    },
    {
      label: "Haargesundheit",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-12.webp",
      url: "/produktsuche/anwendung/haargesundheit-haarwachstum",
    },
    {
      label: "Hautgesundheit",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-13.webp",
      url: "/produktsuche/anwendung/hautgesundheit-nagelgesundheit-haut-nagelpflege",
    },
    {
      label: "PMS Syndrom",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ_PMS.webp",
      url: "/produktsuche/anwendung/menstruation-pms-praemenstruellen-syndrom",
    },
    {
      label: "Potenz / Erektion",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-06.webp",
      url: "/produktsuche/anwendung/potenz-erektion",
    },
    {
      label: "Schlaffördernd",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-02.webp",
      url: "/produktsuche/anwendung/schlaffoerdernd",
    },
    {
      label: "Immunsystem",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-05.webp",
      url: "/produktsuche/anwendung/immunsystem-staerken",
    },
    {
      label: "Schwangerschaft",
      img: "/img/anwendungsgebiete/01_24_CO_Spplemento_RZ-14.webp",
      url: "/produktsuche/anwendung/schwangerschaft",
    },
  ];

  return (
    (isMobile || isTabletV) && (
      <ApplicationIndicationItems
        title="Anwendungsgebiete / Indikationen"
        applicationIndications={applicationIndications}
        itemWidth={165}
      />
    )
  );
};

const calculateDynamicHours = (startHours, startDate) => {
  const start = new Date(startDate);
  const now = new Date();
  const millisecondsPerWeek = 1000 * 60 * 60 * 24 * 7;
  const weeksPassed = Math.floor((now - start) / millisecondsPerWeek);
  let totalHours = startHours;

  for (let i = 0; i < weeksPassed; i++) {
    totalHours += i % 2 === 0 ? 60 : 80; // Alternating between adding 15 and 20 hours
  }

  return totalHours;
};

const HighlightCircles = () => {
  const startHours = 12600; // Initial number of hours
  const startDate = "2024-04-01"; // Start date from which hours start accumulating

  // Function to calculate the dynamic hours since the start date
  const dynamicHours = calculateDynamicHours(startHours, startDate);

  return (
    <div>
      <HighlightCircle
        number={window.totalProductCount}
        suffixText="bewertete Produkte"
      />
      <HighlightCircle
        number={window.uniqueIngredientCount}
        suffixText="bewertete Mikronährstoffe"
      />
      <HighlightCircle
        prefixText="Über"
        number={61}
        suffixText="Bewertungs-kriterien"
      />
      <HighlightCircle
        number={window.uniqueBrandCount}
        suffixText="bewertete Marken"
      />
      <HighlightCircle
        number={window.uniqueProductApplicationCount}
        suffixText="Anwendungs-gebiete"
      />
      <HighlightCircle
        prefixText="Über"
        number={dynamicHours}
        suffixText="Arbeitsstunden"
      />
    </div>
  );
};

render(
  <ApplicationIndicationContainer />,
  "applicationIndicationContainerMobile"
);

render(<InfoGraphicContainer />, "infoGraphicContainerMobile");

render(
  <ProductPreviewContainer
    title="Die meistgesuchten Nahrungsergänzungsmittel"
    fetchFunction={fetchTopSearchedProducts}
    scoreType="supScore"
    previewItemWidth={230}
  />,
  "topSearchedProductsContainerMobile"
);
render(
  <ProductPreviewContainer
    title="Nahrungsergänzungsmittel mit der besten Gesamtbewertung"
    fetchFunction={fetchBestSupScoreProducts}
    scoreType="supScore"
    previewItemWidth={230}
  />,
  "bestSupScoreProductsContainerMobile"
);

render(<HighlightCircles />, "highlightCircles");
